import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { ModalContext } from "../../Components/Modals";
import ModalConfirmCof, {
  ModalConfirmCofElementProps,
} from "../../Components/Modals/ModalConfirmCof/modal-confirm-cof";
import { accessService, cacheService, userService } from "../../Core/Api/services";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { LoginResponse } from "../../Core/Api/Types/login-response";
import { PlatformMode } from "../../Core/Api/Types/platform-mode";
import LogLayout from "../../Layouts/Log";
import { InitState } from "../../Redux/Reducers/redux-slice-asset";
import { SetSidebarOpen } from "../../Redux/Reducers/redux-slice-settings-users";
import { routePaths } from "../../Router/router";
import { UserHasAcl } from "../../Utils/helpers";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";

type LoginStateType =
  | "loading"
  | "key-not-found"
  | "revoked"
  | "expired"
  | "user-not-found"
  | "user-deleted"
  | "user-not-valid"
  | "ok"
  | "error";

const LoginExternalAccess = () => {
  const { t } = useTranslation();
  const params = useParams();
  const accessKey = params.key;

  const [loginState, setLoginState] = useState<LoginStateType>("loading");
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const [platformId, setPlatformId] = useState(0);
  const [accessCreatorName, setAccessCreatorName] = useState("");
  const [guestUserEmail, setGuestUserEmail] = useState("");
  const [nbDaysTillExpiration, setNbDaysTillExpiration] = useState(0);
  const [cofRequired, setCofRequired] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const dispatch = useDispatch();

  // Modals
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const onLoginSuccessCallback = (response: LoginResponse) => {
    userService.SetDataOnLoginSuccess().then(async function () {
      dispatch(SetSidebarOpen(false)); // Toggle sidebar to close because there won't be any menu items for external users
      if (response.group?.acls?.includes(AclTypes.aclLockEverything)) {
        navigate(routePaths.lockedUser.path);
        return;
      }

      if (
        cacheService.IsDriveEnabled() &&
        (UserHasAcl(AclTypes.aclDrive) || cacheService.GetPlatformMode() === PlatformMode.Browser) &&
        response.platformHeadObjectId
      ) {
        cacheService.SetPlatformDriveHeadId(response.platformHeadObjectId);
      }
      dispatch(InitState());
      navigate(routePaths.drive.path);
    });
  };

  const firstUserNotValidCallback = () => {
    if (!accessKey) return;

    // Fetch access data without auth, then setLoginState
    accessService
      .GetExternalAccessNoAuth(accessKey)
      .then((response) => {
        console.log(response);
        if (response.success) {
          setPlatformId(response.platformId);
          setAccessCreatorName(response.creatorName);
          setGuestUserEmail(response.email);
          setNbDaysTillExpiration(response.daysTillExpiration);
          setCofRequired(response.cofRequired);
          setFirstName(response.firstName || "");
          setLastName(response.lastName || "");
          setLoginState("user-not-valid");
        } else {
          if (response.rescode === -1) setLoginState("key-not-found");
          else if (response.rescode === -2) setLoginState("revoked");
          else if (response.rescode === -3) setLoginState("expired");
          else if (response.rescode === -4) setLoginState("user-not-found");
          else if (response.rescode === -5) setLoginState("user-deleted");
          else {
            enqueueCustomSnackbar(t("alert:error.fetchAccessData"), "error");
            setLoginState("error");
          }
        }
      })
      .catch(() => {
        setLoginState("error");
      });
  };

  const loginByAccessKey = (userNotValidCallback?: () => void) => {
    if (!accessKey) return;

    userService.LoginByAccessKey(accessKey).then((response) => {
      if (response.success) {
        // login success callback
        onLoginSuccessCallback(response);
      } else {
        // login failed
        if (response.rescode === -1) setLoginState("key-not-found");
        else if (response.rescode === -2) setLoginState("revoked");
        else if (response.rescode === -3) setLoginState("expired");
        else if (response.rescode === -4) setLoginState("user-not-found");
        else if (response.rescode === -5) setLoginState("user-deleted");
        else if (response.rescode === -6) {
          userNotValidCallback && userNotValidCallback();
        } else setLoginState("error");
      }
    });
  };

  const handleAccessClick = () => {
    if (cofRequired)
      openModal &&
        openModal<ModalConfirmCofElementProps>(ModalConfirmCof, {
          platformId: platformId,
          onConfirm: () => validateUserInputs(),
        });
    else validateUserInputs();
  };

  const validateUserInputs = () => {
    setIsButtonLoading(true);

    accessService.ValidateExternalAccess({ key: accessKey!, firstName, lastName }).then((response) => {
      if (response.success) {
        loginByAccessKey();
      } else {
        if (response.rescode === -1) setLoginState("key-not-found");
        else if (response.rescode === -2) setLoginState("revoked");
        else if (response.rescode === -3) setLoginState("expired");
        else if (response.rescode === -4) setLoginState("user-not-found");
        else if (response.rescode === -5) enqueueCustomSnackbar(t("alert:error.validateUserInputs"), "error");
        else setLoginState("error");

        setIsButtonLoading(false);
      }
    });
  };

  // Main loader
  useEffect(() => {
    if (!accessKey) {
      setLoginState("key-not-found");
      enqueueCustomSnackbar(t("alert:error.invalidAccessKey"), "error");
      return;
    }

    loginByAccessKey(firstUserNotValidCallback);
  }, [accessKey]);

  return (
    <LogLayout
      sxRightContainer={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
      catchSentence={cacheService.GetCatchPhrase()}
    >
      {loginState !== "user-not-valid" && (
        <Box display="flex" alignItems="center" justifyContent="space-around" width="100%" height="100%">
          {loginState === "loading" && <CircularProgress size={80} />}
          {loginState === "key-not-found" && (
            <Typography variant="h3" textAlign="center">
              {t("page:error.accessKeyNotFound")}
            </Typography>
          )}
          {loginState === "revoked" && (
            <Typography variant="h3" textAlign="center">
              {t("page:error.AccessHasBeenRevoked")}
            </Typography>
          )}
          {loginState === "expired" && (
            <Typography variant="h3" textAlign="center">
              {t("page:error.accessIsExpired")}
            </Typography>
          )}
          {loginState === "user-not-found" && (
            <Typography variant="h3" textAlign="center">
              {t("page:error.guestUserNotFound")}
            </Typography>
          )}
          {loginState === "user-deleted" && (
            <Typography variant="h3" textAlign="center">
              {t("page:error.guestUserHasBeenDeleted")}
            </Typography>
          )}
          {loginState === "error" && (
            <Typography variant="h3" textAlign="center">
              {t("alert:error.oopsSomethingWentWrongWithDot")}
            </Typography>
          )}
        </Box>
      )}

      {loginState === "user-not-valid" && (
        <>
          <Stack alignItems="center">
            <Typography variant="h3" textAlign="center">
              {t("page:body.externalAccessLogin")}
            </Typography>
            <Typography color="text.secondary" variant="subtitle2" textAlign="center">
              {t("page:body.youHaveBeenInvitedByCreator", { name: accessCreatorName })}
            </Typography>
          </Stack>
          <Divider sx={{ mt: 4, mb: 4 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel required shrink htmlFor="email" sx={{ flex: 1 }}>
              {t("field:label.email")}
            </InputLabel>
            <OutlinedInput size="large" value={guestUserEmail} id="email" disabled />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel required shrink htmlFor="firstName" sx={{ flex: 1 }}>
              {t("common:firstName")}
            </InputLabel>
            <OutlinedInput
              size="large"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isButtonLoading}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 5 }}>
            <InputLabel required shrink htmlFor="lastName" sx={{ flex: 1 }}>
              {t("common:lastName")}
            </InputLabel>
            <OutlinedInput
              size="large"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isButtonLoading}
            />
          </FormControl>
          <Stack flexWrap={"nowrap"} justifyContent={"space-around"} width="100%" alignItems={"center"}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              loading={isButtonLoading}
              loadingIndicator={<CircularProgress color="inherit" size={25} />}
              disabled={!firstName.length || !lastName.length}
              onClick={handleAccessClick}
            >
              {t("common:access")}
            </Button>
            <Box mt={5}>
              <Typography color="text.secondary" variant="subtitle2" textAlign="center">
                {t("page:body.keepLinkEmailInstruction")}
              </Typography>
              <Typography color="text.secondary" variant="subtitle2" textAlign="center">
                {t("page:body.accessWillExpireInCountDays", { count: nbDaysTillExpiration })}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </LogLayout>
  );
};

export default LoginExternalAccess;
